import _ from 'lodash'
import CONSTANTS from 'core/data-model/constants/values'

import { isNil, isNotNil } from 'core/service/lib/type-check'
import { createSelector } from 'reselect'
import {
  getSelectedDisplayProduct,
  getDisplayProducts,
  getSelectedDisplayProductCode,
} from 'quick-quote/product-selection/selectors'
import { canProceedToCoveragePlan } from 'quick-quote/selectors/next-tab-proceeding'

import { getAge } from 'quick-quote/insured-information/selectors'

import {
  getSumAssured$,
  getSelectedRiders,
  getBasicPremium$,
  getTotalPremium$,
  getBasicPlanErrorMessage,
  getPensionType,
} from 'quick-quote/product-common/coverage-plan/selectors'

export const canProceedBumnanReadyBenefitIllustration = createSelector(
  getSumAssured$,
  getBasicPremium$,
  getTotalPremium$,
  getBasicPlanErrorMessage,
  getSelectedRiders,
  canProceedToCoveragePlan,
  getSelectedDisplayProduct,
  getPensionType,
  (
    sumAssured$,
    basicPremium,
    totalPremium$,
    basicPlanErrorMessage,
    selectedRiders,
    canProceedToCoveragePlan,
    selectedDisplayProduct,
    pensionType
  ) => {
    const selectedRiderInvalidMessages = selectedRiders.filter((rider) =>
      isNotNil(rider.errors.find((error) => error.type === CONSTANTS.VALIDATION))
    )

    const canProceed =
      canProceedToCoveragePlan &&
      sumAssured$.isOk &&
      basicPremium.isOk &&
      totalPremium$.isOk &&
      !_.isEmpty(pensionType) &&
      _.isEmpty(selectedRiderInvalidMessages) &&
      _.isEmpty(basicPlanErrorMessage)

    if (selectedDisplayProduct.type === CONSTANTS.CAMPAIGN_PRODUCT_TYPE) {
      const selectedRiderCodes = selectedRiders.map((rider) => rider.code)
      return (
        canProceed &&
        selectedDisplayProduct.requiredRiders != null &&
        selectedDisplayProduct.requiredRiders.every((riderCode) => {
          return selectedRiderCodes.indexOf(riderCode) > -1
        })
      )
    }

    return canProceed
  }
)

const _isValidAge = (entryAge, age) => {
  const { minimum, maximum } = entryAge
  const insureAge = age.unit !== 'year' ? 0 : age.value
  return insureAge >= minimum.value && insureAge <= maximum.value
}

export const getSelectableProductBumnanReady = createSelector(getAge, getDisplayProducts, (age, products) => {
  const productGroup = _.filter(products, (product) =>
    _.includes(CONSTANTS.BUMNAN_READY_CODE_GROUP, _.get(product, 'code'))
  )
  const validProducts = _.filter(productGroup, (product) => _isValidAge(_.get(product, 'entryAge'), age))
  const validProductCodes = validProducts.map((product) => product.code)
  return validProductCodes
})

export const getSelectedProductBumnanReady = (selectableProductBumnanReady, selectedProduct) => {
  // if current is not valid return the first valie
  return selectableProductBumnanReady.includes(selectedProduct) ? selectedProduct : selectableProductBumnanReady[0]
}

// -------------- BUMNAN SMART --------------
export const getAvaliableProductBumnanSmart = createSelector(getAge, getDisplayProducts, (age, products) => {
  const productGroup = _.filter(products, (product) => _.includes(CONSTANTS.BUMNAN_SMART_GROUP, _.get(product, 'code')))
  const validProducts = _.filter(productGroup, (product) => _isValidAge(_.get(product, 'entryAge'), age))
  return validProducts
})

export const getAvaliableProductKoomverRetire = createSelector(getAge, getDisplayProducts, (age, products) => {
  const productGroup = _.filter(products, (product) =>
    _.includes(CONSTANTS.KOOMVER_RETIRE_GROUP, _.get(product, 'code'))
  )
  const validProducts = _.filter(productGroup, (product) => _isValidAge(_.get(product, 'entryAge'), age))
  return validProducts
})

export const getPensionAge = (state) => state.coveragePlan.pensionAge

export const getSelectedBumnanSmartPlanGroup = createSelector(getSelectedDisplayProductCode, (selectedProductCode) =>
  CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6.includes(selectedProductCode)
    ? CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6
    : CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_AGE
)

export const getAvaliablePensionAgePlan = createSelector(
  getAvaliableProductBumnanSmart,
  getSelectedBumnanSmartPlanGroup,
  (avaliableProductBumnanSmart, selectedBumnanSmartPlanGroup) => {
    const filterSelectedBumnanSmartPlanGroup = _.filter(avaliableProductBumnanSmart, (product) =>
      _.includes(selectedBumnanSmartPlanGroup, _.get(product, 'code'))
    )

    const listAvaliablePensionAge = filterSelectedBumnanSmartPlanGroup.map((product) => product.pensionAge)
    return listAvaliablePensionAge
  }
)

export const getAvaliablePensionAgePlanKoomverRetire = createSelector(
  getAvaliableProductKoomverRetire,
  (avaliableProductKoomverRetire) => {
    return avaliableProductKoomverRetire.map((product) => product.pensionAge)
  }
)

export const getActivePensionAgePlan = createSelector(
  getAvaliablePensionAgePlan,
  getPensionAge,
  (avaliablePensionAge, pensionAge) => (avaliablePensionAge.includes(pensionAge) ? pensionAge : avaliablePensionAge[0])
)

export const getActivePensionAgePlanKoomverRetire = createSelector(
  getAvaliablePensionAgePlanKoomverRetire,
  getPensionAge,
  (avaliablePensionAge, pensionAge) => (avaliablePensionAge.includes(pensionAge) ? pensionAge : avaliablePensionAge[0])
)

export const getAvaliableProductCodeBumnanSmartPlan = createSelector(
  getAvaliableProductBumnanSmart,
  getSelectedBumnanSmartPlanGroup,
  (avaliableProductBumnanSmart, selectedBumnanSmartPlanGroup) => {
    const filterSelectedBumnanSmartPlanGroup = _.filter(avaliableProductBumnanSmart, (product) =>
      _.includes(selectedBumnanSmartPlanGroup, _.get(product, 'code'))
    )

    const listAvaliableProductCodeBumnanSmart = filterSelectedBumnanSmartPlanGroup.map((product) => product.code)
    return listAvaliableProductCodeBumnanSmart
  }
)

export const getActiveProductCodeBumnanSmart = createSelector(
  getAvaliableProductCodeBumnanSmartPlan,
  getSelectedDisplayProductCode,
  (avaliableProductCodeBumnanSmart, selectedProductCode) => {
    if (avaliableProductCodeBumnanSmart.includes(selectedProductCode)) {
      return CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6.includes(selectedProductCode)
        ? CONSTANTS.BUMNAN_SMART_DEFAULT_CODE.BUMNAN_SMART_6
        : CONSTANTS.BUMNAN_SMART_DEFAULT_CODE.BUMNAN_SMART_AGE
    } else {
      return CONSTANTS.BUMNAN_SMART_DEFAULT_CODE.BUMNAN_SMART_6
    }
  }
)
